import { useState } from "react";
import api from "@/lib/api";
import { useAuth, formatErr } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";

/**
 * Forced password change gate. Rendered by <Protected> whenever the resolved
 * user carries `must_change_password: true` — typically the four seeded
 * Functional LAB clinicians on their very first login. Blocks access to every
 * dashboard route until they choose a fresh secret.
 */
export default function ForcePasswordChange() {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) { setErr("Passwords do not match"); return; }
    if (next.length < 8) { setErr("New password must be at least 8 characters"); return; }
    if (next === current) { setErr("New password must be different from your temporary one"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      // Clear the flag locally and re-fetch /auth/me to make sure downstream state is fresh.
      const { data } = await api.get("/auth/me", { withCredentials: true });
      setUser(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="absolute top-6 right-6 flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center"><Logo variant="wordmark" size="lg" /></div>
        <div className="fl-card p-8" data-testid="force-password-change-card">
          <div className="text-[11px] tracking-[3px] uppercase font-bold" style={{ color: "#f47a20" }}>First-time sign-in</div>
          <h1 className="fl-heading text-3xl mt-2">Set a new password</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--fl-muted)" }}>
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Your account was created with a temporary password.
            Choose a new one to continue.
          </p>
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <div className="fl-label mb-2">Temporary password</div>
              <input className="fl-input" type="password" required autoFocus value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="fpc-current" />
            </div>
            <div>
              <div className="fl-label mb-2">New password</div>
              <input className="fl-input" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="fpc-new" />
              <div className="mt-1 text-xs" style={{ color: "var(--fl-muted-2)" }}>Minimum 8 characters. Use a mix of letters and numbers.</div>
            </div>
            <div>
              <div className="fl-label mb-2">Confirm new password</div>
              <input className="fl-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="fpc-confirm" />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="fpc-error">{err}</div>}
            <button className="fl-btn w-full justify-center" disabled={loading} data-testid="fpc-submit">
              {loading ? "Saving…" : "Save and continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
